'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '@/contexts/AuthContext';
import type { Permit, PermitInspection } from './types';

interface InspectionScheduleModalProps {
  permit: Permit | null;
  onClose: () => void;
  onScheduled: () => void;
}

const INSPECTION_TYPES: Array<{ key: PermitInspection['type']; label: string; color: string }> = [
  { key: 'mid-roof',     label: 'Mid-Roof',      color: '#2563eb' },
  { key: 'final',        label: 'Final',         color: '#16a34a' },
  { key: 'reinspection', label: 'Re-inspection', color: '#F5A623' },
  { key: 'other',        label: 'Other',         color: '#6b7280' },
];

const InspectionScheduleModal = ({ permit, onClose, onScheduled }: InspectionScheduleModalProps) => {
  const [type, setType] = useState<PermitInspection['type']>('final');
  const [date, setDate] = useState<string>('');
  const [time, setTime] = useState<string>('09:00');
  const [inspector, setInspector] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [submitting, setSubmitting] = useState(false);

    const { currentUser } = useAuth();

  useEffect(() => {
    if (!permit) return;
    setType('final');
    const d = new Date();
    d.setDate(d.getDate() + 3);
    setDate(d.toISOString().slice(0, 10));
    setTime('09:00');
    setInspector('');
    setNotes('');
    setSubmitting(false);
  }, [permit?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!permit) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape' && !submitting) onClose(); };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [permit, submitting, onClose]);

  if (!permit) return null;

  const isValid = !!date && !!time && inspector.trim().length > 0 && !submitting;

  const handleSubmit = async () => {
    if (!isValid || !permit) return;
    setSubmitting(true);

    const scheduledAt = new Date(`${date}T${time}:00`).toISOString();
    const newInspection: PermitInspection = {
      id: (crypto?.randomUUID?.() ?? `ins-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
      type,
      scheduled_at: scheduledAt,
      inspector: inspector.trim(),
      result: 'pending',
      corrections: null,
      notes: notes.trim() || null,
      completed_at: null,
    };

    const existing: PermitInspection[] = Array.isArray(permit.inspections) ? permit.inspections : [];
    const updatedInspections = [...existing, newInspection];

    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();
    const now = new Date().toISOString();
    const existingTimeline = Array.isArray(permit.timeline_log) ? permit.timeline_log : [];
    const newTimelineEntry = {
      action: 'inspection_scheduled',
      actor: actorName,
      actor_id: currentUser?.id ?? null,
      timestamp: now,
      note: `${type} inspection scheduled for ${new Date(scheduledAt).toLocaleString()} with ${inspector.trim()}`,
    };

    const shouldAdvanceStatus = permit.status === 'approved';
    const updatePayload: Record<string, unknown> = {
      inspections: updatedInspections,
      timeline_log: [...existingTimeline, newTimelineEntry],
      updated_at: now,
    };
    if (shouldAdvanceStatus) updatePayload.status = 'scheduled_inspection';

    try {
      const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL!;
      const supabaseKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!;

      const response = await fetch(
        `${supabaseUrl}/rest/v1/permits?id=eq.${encodeURIComponent(permit.id)}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`,
            'Prefer': 'return=minimal',
          },
          body: JSON.stringify(updatePayload),
        }
      );

      if (!response.ok) {
        const errText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errText}`);
      }

      toast.success(
        `${type === 'mid-roof' ? 'Mid-roof' : type === 'final' ? 'Final' : type === 'reinspection' ? 'Re-inspection' : 'Other'} inspection scheduled`,
        { duration: 3500, icon: '📅' }
      );
      onScheduled();
      setSubmitting(false);
      onClose();
    } catch (err) {
      console.error('Failed to schedule inspection:', err);
      toast.error(`Schedule failed: ${err instanceof Error ? err.message : 'Unknown'}`, { duration: 6000 });
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[110] flex items-center justify-center p-4"
      onClick={submitting ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="schedule-inspection-title"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        <div className="p-5 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h3 id="schedule-inspection-title" className="text-lg font-bold text-[#1C2E5E]">Schedule Inspection</h3>
              <p className="text-[12px] text-gray-500 mt-0.5 font-mono">{permit.job_id} · {permit.jurisdiction_name ?? '—'}</p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
              aria-label="Close modal"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round"/>
              </svg>
            </button>
          </div>
        </div>

        <div className="p-5 space-y-4">
          {/* Type */}
          <div>
            <label className="block text-[12px] font-semibold text-gray-700 mb-1.5">Type</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {INSPECTION_TYPES.map(opt => {
                const active = type === opt.key;
                return (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setType(opt.key)}
                    className={`px-2 py-2 rounded-lg text-[11px] font-bold transition-all ${
                      active ? 'text-white ring-2 ring-offset-1' : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                    }`}
                    style={active ? { background: opt.color, boxShadow: `0 0 0 2px ${opt.color}33` } : {}}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Date + Time */}
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label htmlFor="ins-date" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
                Date <span className="text-red-500">*</span>
              </label>
              <input
                id="ins-date"
                type="date"
                value={date}
                onChange={e => setDate(e.target.value)}
                className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
              />
            </div>
            <div>
              <label htmlFor="ins-time" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
                Time <span className="text-red-500">*</span>
              </label>
              <input
                id="ins-time"
                type="time"
                value={time}
                onChange={e => setTime(e.target.value)}
                className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
              />
            </div>
          </div>

          {/* Inspector */}
          <div>
            <label htmlFor="ins-inspector" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Inspector Name <span className="text-red-500">*</span>
            </label>
            <input
              id="ins-inspector"
              type="text"
              value={inspector}
              onChange={e => setInspector(e.target.value)}
              placeholder="e.g. Inspector Martinez"
              maxLength={80}
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all"
            />
          </div>

          {/* Notes */}
          <div>
            <label htmlFor="ins-notes" className="block text-[12px] font-semibold text-gray-700 mb-1.5">
              Notes <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              id="ins-notes"
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder="Pre-inspection notes, special instructions, or access details"
              className="w-full px-3 py-2 text-[13px] rounded-lg border border-gray-200 bg-gray-50 focus:bg-white focus:border-[#E89A3C] focus:outline-none focus:ring-2 focus:ring-[#E89A3C]/20 transition-all resize-none"
            />
            <p className="text-[11px] text-gray-400 mt-1">{notes.length}/500</p>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 p-5 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors">
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white bg-[#1C2E5E] hover:bg-[#022b80] disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
          >
            {submitting && (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            )}
            {submitting ? 'Scheduling…' : 'Schedule Inspection'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default InspectionScheduleModal;
